'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, square, stack, vstack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

export const AprBreakdown = ({ currency }: { currency: string }) => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { baseAprPct, extraAprs, sumOfExtraAprPct } = getEarnSummaryOfCurrency(currency, earnSummary);

  return (
    <details className={css({ borderTopWidth: '1px', borderTopStyle: 'solid', borderColor: 'border.default.level-1' })}>
      <summary
        className={hstack({
          h: 48,
          cursor: 'pointer',
          userSelect: 'none',
          justify: 'space-between',
        })}
      >
        <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Your_APR_breakdown' />
        </div>
        <span
          className={square({
            size: 24,
            color: 'content.default.level-3',
            '[open] &': { transform: 'rotate(-180deg)' },
          })}
        >
          <ChevronDownIcon size={16} />
        </span>
      </summary>
      <div className={stack({ pb: 12 })}>
        <div className={hstack({ h: 28 })}>
          <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3', flexGrow: 1 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Base_APR' />
          </div>
          <Formatted.Percent
            value={baseAprPct}
            precision={2}
            css={{ textStyle: 'Text/M/Regular' }}
            withColor
            withPlus
            toFixed
            suffix='APR'
          />
        </div>
        <div className={hstack({ h: 28 })}>
          <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3', flexGrow: 1 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Extra_APR' />
          </div>
          <Formatted.Percent
            value={sumOfExtraAprPct}
            precision={2}
            css={{ textStyle: 'Text/M/Regular' }}
            withColor
            withPlus
            toFixed
            suffix='APR'
          />
        </div>
        {extraAprs.length > 0 && (
          <div
            className={hstack({
              gap: 4,
              overflowX: 'auto',
              userSelect: 'none',
              justifyContent: 'flex-start',
            })}
          >
            {extraAprs.map((extraApr) => (
              <div
                className={vstack({ gap: 2, bg: 'background.default.level-3', p: 8, rounded: 8 })}
                key={extraApr.title}
              >
                <div
                  data-apr-style={extraApr.title}
                  className={center({
                    textStyle: 'Text/XXS/Strong',
                    color: 'inverse.black',
                    minW: 64,
                    h: 16,
                    rounded: 4,
                    px: 4,
                    whiteSpace: 'nowrap',
                    bg: 'inverse.white',
                    '&[data-apr-style=VIP]': {
                      bgGradient: 'programs.vip',
                    },
                  })}
                >
                  {extraApr.title}
                </div>
                <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
                  {Num(extraApr.appliedApr).multipliedBy(100).toFixed(2)}%
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};
